// Section 25 (M5d) and Section 9: the steward's build queue. Repairs first (a building below
// the repair threshold), then new buildings in priority order when materials allow: a field when
// the farmers it wants exceed the plots, a well, a granary, a shelter when beds are short, a
// woodshed, a workshop, a clinic, a school and a common hall. At most one order per season and a
// short queue, so builders finish what they start.
import { buildingCount } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type { BuildCommand, BuildingTypeId } from '../../core/types';
import { buildLimit } from '../validate-decision';
import { bedsOf } from './answer';
import type { BuildNeeds, Materials } from './crew';
import { plotsWanted, seasonOf, totalPlots, warmthDemand, workers, type StewardCtx } from './model';

export interface BuildPlan {
  commands: BuildCommand[];
  needs: BuildNeeds;
}

function materialsOf(sc: StewardCtx, type: BuildingTypeId): Materials {
  const m = sc.ctx.registry.buildings[type].materials;
  return { wood: m.wood ?? 0, stone: m.stone ?? 0 };
}

/** Firewood to hold back from building for the cold seasons still ahead this year. */
function woodReserve(sc: StewardCtx): number {
  let reserve = 0;
  for (let k = 0; k < sc.horizon; k++) {
    const season = seasonOf(sc, k);
    if (season === 'spring' && k > 0) break;
    reserve += warmthDemand(sc, k);
  }
  return reserve;
}

/** Stone houses instead of shelters from a year on, when the stock pays for one. */
function stoneForHouse(sc: StewardCtx): boolean {
  const stone = sc.ctx.registry.buildings.house.materials.stone ?? 0;
  return (
    sc.state.year >= sc.cfg.steward.laterBuildsFromYear && sc.state.stocks.stone.amount >= stone
  );
}

function wants(sc: StewardCtx, type: BuildingTypeId): boolean {
  const s = sc.state;
  const w = sc.cfg.steward;
  const have = buildingCount(s, type);
  switch (type) {
    case 'field': {
      const maxPlots =
        Math.floor(workers(sc).length * w.maxFarmShare) * sc.cfg.jobs.farmer.plotsPerFarmer;
      return totalPlots(sc) < Math.min(plotsWanted(sc), maxPlots);
    }
    case 'well':
      return true;
    case 'granary':
      return (
        have === 0 ||
        (have < w.maxGranaries && s.stocks.food.amount >= s.stocks.food.capacity * 0.8)
      );
    case 'shelter':
      return bedsOf(s, sc.cfg) < sc.pop + w.spareBeds && !stoneForHouse(sc);
    case 'house':
      // M7: once stone flows, beds come as stone houses (0.7x warmth, 0.3x fire, Section 9).
      return bedsOf(s, sc.cfg) < sc.pop + w.spareBeds && stoneForHouse(sc);
    case 'cistern':
      // M7: a store for water once there is a well (Section 7: no evaporation).
      return have === 0 && buildingCount(s, 'well') > 0;
    case 'cemetery':
      return have === 0 && sc.pop >= w.hallMinPopulation && s.year >= w.laterBuildsFromYear;
    case 'watch-post':
      return (
        have === 0 &&
        sc.cfg.threatLevel[s.options.threatLevel] > 0 &&
        s.year >= w.laterBuildsFromYear
      );
    case 'palisade':
      return have === 0 && s.options.threatLevel === 'high' && s.year >= w.laterBuildsFromYear;
    case 'woodshed': {
      let cold = 0;
      for (let k = 0; k < sc.horizon; k++) cold += warmthDemand(sc, k);
      return have === 0 && s.stocks.wood.capacity < cold * 1.25;
    }
    case 'workshop':
      return have === 0;
    case 'clinic':
    case 'common-hall':
      return have === 0 && sc.pop >= w.hallMinPopulation;
    case 'school': {
      const spy = sc.cfg.people.seasonsPerYear;
      const pupils = sc.people.filter((p) => {
        const years = Math.floor(p.ageSeasons / spy);
        return years >= sc.cfg.skills.schoolAgeFromYears && years <= sc.cfg.skills.schoolAgeToYears;
      }).length;
      return have === 0 && pupils >= w.schoolMinChildren;
    }
    default:
      return false;
  }
}

// M7: the granary first (without one an Autumn harvest cannot carry the settlement through
// Winter and Spring, Section 7), then the well (without one water takes a carrier for every six
// people), then the fields next Spring's farmers want (a field finished after the Spring
// planting stands idle for a year) and beds.
const PRIORITY: readonly BuildingTypeId[] = [
  'granary',
  'well',
  'field',
  'shelter',
  'workshop',
  'house',
  'woodshed',
  'clinic',
  'school',
  'common-hall',
  'cistern',
  'watch-post',
  'cemetery',
  'palisade',
];

export function planBuilds(sc: StewardCtx): BuildPlan {
  const s = sc.state;
  const w = sc.cfg.steward;
  const queue = s.buildQueue.filter((o) => !o.paused);
  const owed: Materials = { wood: 0, stone: 0 };
  for (const o of queue) {
    if (o.materialsPaid || o.repairOf !== null) continue;
    const m = materialsOf(sc, o.buildingType);
    owed.wood += m.wood;
    owed.stone += m.stone;
  }
  const commands: BuildCommand[] = [];
  const wanted: Materials = { wood: 0, stone: 0 };
  // M7: materials are earmarked for the first wanted building the stock cannot pay for yet, so a
  // later wish does not take the stone the well is waiting for (the steward saves up).
  const free = (): Materials => ({
    wood: s.stocks.wood.amount - woodReserve(sc) - owed.wood - wanted.wood,
    stone: s.stocks.stone.amount - owed.stone - wanted.stone,
  });
  // M7: a repair is queued whatever the queue holds (Section 20: no building below 50).
  const damaged = sortedValues(s.buildings).find(
    (b) => b.condition < w.repairBelowCondition && !s.buildQueue.some((o) => o.repairOf === b.id),
  );
  if (damaged !== undefined) {
    commands.push({ kind: 'queue', buildingType: damaged.type, repairOf: damaged.id });
  } else if (queue.length < w.maxQueuedOrders) {
    {
      for (const type of PRIORITY) {
        if (s.buildQueue.some((o) => o.buildingType === type && o.repairOf === null)) continue;
        if (buildingCount(s, type) >= buildLimit(s, type, sc.cfg)) continue;
        if (!wants(sc, type)) continue;
        const m = materialsOf(sc, type);
        const f = free();
        if ((m.wood <= 0 || f.wood >= m.wood) && (m.stone <= 0 || f.stone >= m.stone)) {
          commands.push({ kind: 'queue', buildingType: type, repairOf: null });
          owed.wood += m.wood;
          owed.stone += m.stone;
          break;
        }
        // Gather for the first wanted building the stock cannot pay for yet.
        if (wanted.wood === 0 && wanted.stone === 0) {
          wanted.wood = m.wood;
          wanted.stone = m.stone;
        }
      }
    }
  }
  const buildable =
    queue.some((o) => o.materialsPaid) ||
    commands.length > 0 ||
    queue.some((o) => {
      if (o.repairOf !== null) return true;
      const m = materialsOf(sc, o.buildingType);
      return s.stocks.wood.amount >= m.wood && s.stocks.stone.amount >= m.stone;
    });
  return {
    commands,
    needs: {
      buildable,
      owed,
      wanted,
    },
  };
}
